import { createContext, useContext, type ReactNode } from 'react';
import { findCountry } from '@repletoryreach/sdk-utils/phone';

const PhoneDefaultsContext = createContext<string | null>(null);

export interface PhoneDefaultsProviderProps {
  /**
   * The country to assume for a phone number typed without a country code,
   * as ISO 3166-1 alpha-2. The consoles pass the signed-in person's own
   * preference when they have one, else the organisation's default country.
   */
  country?: string | null;
  children?: ReactNode;
}

/** Gives every `PhoneInput` and `CountryPicker` below it a default country. */
export function PhoneDefaultsProvider({ country, children }: PhoneDefaultsProviderProps): ReactNode {
  return <PhoneDefaultsContext.Provider value={country ?? null}>{children}</PhoneDefaultsContext.Provider>;
}

/** The region of the browser's language ("en-ZA" → ZA), when it names a country the table knows. */
export function browserCountry(): string | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const region = (navigator.language ?? '').split(/[-_]/)[1];
  return findCountry(region)?.iso;
}

/** The last resort when nothing else says which country to assume. */
export const FALLBACK_COUNTRY = 'ZA';

/**
 * Which country a phone field should assume, in order: the explicit prop,
 * the provider (the person's preference, then the organisation's default),
 * the browser's locale, then South Africa.
 */
export function usePhoneDefaultCountry(explicit?: string | null): string {
  const fromProvider = useContext(PhoneDefaultsContext);
  return findCountry(explicit)?.iso ?? findCountry(fromProvider)?.iso ?? browserCountry() ?? FALLBACK_COUNTRY;
}
